import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {capitalizeString} from 'utilities';
import {Text, Flex, IconType, Section} from 'components/UIKit';
import {SocialLinks} from '../../types';
import * as locales from './locales';
import {SocialIconLink, PopupDimensions} from './components/SocialIconLink';
import styles from './ListingShare.scss';

export interface ListingShareProps {
  links: SocialLinks;
}

export interface LinkDescriptor {
  alt: string;
  name: string;
  icon: string;
  dimensions?: PopupDimensions;
}

// Dimensions pulled from manual testing of downstream login pages
function getLinks(i18n): LinkDescriptor[] {
  return [
    {
      name: 'facebook',
      icon: 'facebook',
      alt: i18n.translate('shareFacebook'),
      dimensions: {
        height: 300,
        width: 600,
      },
    },
    {
      name: 'twitter',
      icon: 'twitter',
      alt: i18n.translate('shareTwitter'),
      dimensions: {
        height: 448,
        width: 500,
      },
    },
    {
      name: 'linkedIn',
      icon: 'linkedin',
      alt: i18n.translate('shareLinkedin'),
      dimensions: {
        height: 600,
        width: 1000,
      },
    },
    {
      name: 'email',
      icon: 'email',
      alt: i18n.translate('shareEmail'),
    },
  ];
}

export interface ListingShareState {
  linksToDisplay: LinkDescriptor[];
}

class ListingShareComponent extends React.Component<
  ListingShareProps & WithI18nProps,
  ListingShareState
> {
  constructor(props) {
    super(props);
    const {links} = this.props;
    this.state = {
      linksToDisplay: getLinks(props.i18n).filter((link) => links[link.name]),
    };
  }

  handlePrintClick = () => {
    if (window.print) {
      window.print();
    }
  };

  render() {
    const {i18n, links} = this.props;
    const {linksToDisplay} = this.state;

    // @TODO: Bring back with print stylesheets
    const showPrint = false;

    return (
      <Section spacing="small" fullWidth>
        <Flex className={styles.Container} alignItems="center" justifyContent="space-between">
          <Text
            resetMargin
            type="h5"
            text={i18n.translate('share')}
            className={styles.Share}
            component="h2"
          />
          <ul className={styles.List}>
            {linksToDisplay.map((link) => (
              <li key={link.name}>
                <SocialIconLink
                  alt={link.alt}
                  key={link.name}
                  link={links[link.name]}
                  icon={link.icon as IconType}
                  iconSize={20}
                  platform={link.name}
                  className={styles[capitalizeString(link.name)] || ''}
                  popupDimensions={link.dimensions}
                />
              </li>
            ))}
            {showPrint && (
              <li className={styles.Print}>
                <SocialIconLink
                  alt={i18n.translate('printAlt')}
                  icon="print"
                  onClick={this.handlePrintClick}
                  iconSize={20}
                />
              </li>
            )}
          </ul>
        </Flex>
      </Section>
    );
  }
}

export const ListingShare = withI18n({
  id: 'ListingShare',
  fallback: locales.en,
})(ListingShareComponent);

export default ListingShare;
